/**
 * Two-letter initials for the account avatar.
 *
 * Lived inline in routes/Settings.tsx, which was the app's only avatar surface. It
 * moved out when the avatar became a component, and then out of that component too:
 * it is a pure string function with no React in it, so it belongs here with the rest
 * of them — directly unit-testable, and reachable by a test that has no reason to
 * render a circle to find out what an empty name draws.
 *
 * ============================================================================
 * THE FALLBACK IS NOT SOMEBODY'S INITIALS ANY MORE. It used to be "GE" — the demo
 * account's own initials, carried over from the prototype — which meant an avatar with
 * no name behind it drew a specific person's monogram. Batch 7 §5 forbids the frontend
 * depending on a hardcoded identity, and while two letters look harmless, the reachable
 * case was not: `selectProfileName` returned "Goodluck Efe" for any signed-out read, so
 * the fallback and the selector agreed on an identity the app had invented.
 *
 * The selector now returns '' and this returns a NEUTRAL MARK. An empty name has no
 * initials, and the honest answer to "whose avatar is this?" when nobody is signed in is
 * not a monogram at all.
 * ============================================================================
 */

/**
 * The fallback when a name yields no letters at all.
 *
 * A BULLET, NOT A LETTER PAIR. It reads as "no name" rather than as somebody's initials,
 * it centres in the circle at every size the avatar is drawn at, and it cannot be
 * mistaken for a real account the way "GE" could. A person whose name is genuinely
 * unrenderable (all punctuation, say) gets it too, which is the same honest answer.
 */
export const INITIALS_FALLBACK = '•';

/**
 * "Goodluck Efe" -> "GE". One word gives one letter; three give the first two.
 *
 * `.filter(Boolean)` guards a name with a double space in it: `'A  B'.split(' ')`
 * yields an empty middle element, `w[0]` on it is undefined, and joining that
 * produced the string "undefined" — two letters of which then became "UN".
 */
export function initials(name: string): string {
  return (
    name
      .split(' ')
      .map((w) => w[0])
      .filter(Boolean)
      .join('')
      .slice(0, 2)
      .toUpperCase() || INITIALS_FALLBACK
  );
}
